// iConnect — Hot water system selector (flexi-iconnect-hws-selector.php)
// Requires GSAP to be loaded before this file.
//
// Three steps of radio tiles. Each answer fills a blank in the "I need …"
// sentence, updates the match counter and disables tiles in later steps that
// have no matching rows. A tap or click moves straight on to the next step;
// keyboard users (arrow keys change radios) move on with Next. Once all three are answered the matching products
// show beneath the panel and update live as answers change.

document.querySelectorAll('.iconnect-hws').forEach((root) => {
    const rows     = JSON.parse(root.dataset.rows);
    const steps    = [...root.querySelectorAll('.iconnect-hws__step')];
    const names    = steps.map((step) => step.dataset.step);
    const rail     = [...root.querySelectorAll('.iconnect-hws__rail-step')];
    const progress = root.querySelector('.iconnect-hws__progress span');
    const back     = root.querySelector('.iconnect-hws__back');
    const next     = root.querySelector('.iconnect-hws__next');
    const countNum = root.querySelector('.iconnect-hws__count-num');
    const results  = root.querySelector('.iconnect-hws__results');
    const grid     = root.querySelector('.iconnect-hws__grid');
    const products = new Map([...grid.children].map((card) => [Number(card.dataset.product), card]));

    const answers = {};
    let current = 0;
    let motion  = false;
    let pointer = false;   // last answer came from a pointer, not the keyboard

    gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
        motion = true;
        return () => { motion = false; };
    });

    const rowMatches = (row, picks) =>
        (!picks.system_type || row.system_type === picks.system_type) &&
        (!picks.household_size || row.household_size.includes(picks.household_size)) &&
        (!picks.bathrooms || row.bathrooms.includes(picks.bathrooms));

    const matchingProducts = (picks) =>
        [...new Set(rows.filter((row) => rowMatches(row, picks)).flatMap((row) => row.products))];

    const answersBefore = (index) =>
        Object.fromEntries(names.slice(0, index).filter((name) => answers[name]).map((name) => [name, answers[name]]));

    // Disable tiles with no rows for the earlier answers; drop answers that no longer fit.
    const refreshTiles = () => {
        steps.forEach((step, index) => {
            const before = answersBefore(index);
            step.querySelectorAll('input').forEach((input) => {
                input.disabled = !rows.some((row) => rowMatches(row, { ...before, [names[index]]: input.value }));
                if (input.disabled && input.checked) {
                    input.checked = false;
                    delete answers[names[index]];
                }
            });
        });
    };

    const refreshSentence = () => {
        root.querySelectorAll('.iconnect-hws__blank').forEach((blank) => {
            const input = root.querySelector(`.iconnect-hws__step[data-step="${blank.dataset.blank}"] input:checked`);
            blank.textContent = input ? input.dataset.label : blank.dataset.placeholder;
            blank.classList.toggle('is-filled', Boolean(input));
        });
    };

    const refreshCount = () => {
        const total = matchingProducts(answers).length;
        if (!motion) {
            countNum.textContent = total;
            return;
        }
        const counter = { value: Number(countNum.textContent) };
        gsap.to(counter, { value: total, duration: 0.5, ease: 'power2.out', onUpdate: () => { countNum.textContent = Math.round(counter.value); } });
    };

    const refreshResults = () => {
        const complete = names.every((name) => answers[name]);
        const ids = complete ? matchingProducts(answers) : [];
        const wasHidden = results.hidden;

        products.forEach((card) => { card.hidden = true; });
        ids.forEach((id) => {
            grid.append(products.get(id));
            products.get(id).hidden = false;
        });
        results.hidden = !complete;

        if (!complete) return;
        if (wasHidden) {
            results.scrollIntoView({ behavior: motion ? 'smooth' : 'auto', block: 'start' });
            results.querySelector('.iconnect-hws__results-heading').focus({ preventScroll: true });
        }
        if (motion) {
            gsap.from(ids.map((id) => products.get(id)), { y: 30, opacity: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08, clearProps: 'all' });
        }
    };

    const refreshNav = () => {
        rail.forEach((button, index) => {
            button.classList.toggle('is-active', index === current);
            button.classList.toggle('is-done', Boolean(answers[names[index]]));
            button.disabled = names.slice(0, index).some((name) => !answers[name]);
        });
        progress.style.transform = `scaleX(${(current + 1) / steps.length})`;
        back.hidden = current === 0;
        next.hidden = current === steps.length - 1;
        next.disabled = !answers[names[current]];
    };

    const goTo = (index) => {
        const from = steps[current];
        const to   = steps[index];
        const dir  = index > current ? 1 : -1;
        current = index;
        refreshNav();
        if (from === to) return;

        if (!motion) {
            from.hidden = true;
            to.hidden = false;
            return;
        }
        gsap.timeline()
            .to(from, { x: -24 * dir, opacity: 0, duration: 0.2, ease: 'power2.in' })
            .add(() => {
                from.hidden = true;
                gsap.set(from, { clearProps: 'all' });
                to.hidden = false;
            })
            .from(to.querySelector('.iconnect-hws__question'), { x: 24 * dir, opacity: 0, duration: 0.35, ease: 'power3.out' })
            .from(to.querySelectorAll('.iconnect-hws__tile'), { y: 16, opacity: 0, duration: 0.4, ease: 'power3.out', stagger: 0.05, clearProps: 'all' }, '<0.05');
    };

    root.querySelectorAll('.iconnect-hws__blank').forEach((blank) => { blank.dataset.placeholder = blank.textContent.trim(); });

    steps.forEach((step, index) => {
        step.addEventListener('change', (event) => {
            answers[names[index]] = event.target.value;
            refreshTiles();
            refreshSentence();
            refreshCount();
            refreshNav();
            refreshResults();

            if (motion) {
                gsap.fromTo(root.querySelector(`.iconnect-hws__blank[data-blank="${names[index]}"]`), { y: 8, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, ease: 'back.out(2)' });
            }
            if (pointer && index < steps.length - 1) goTo(index + 1);
        });
    });

    root.addEventListener('pointerdown', () => { pointer = true; });
    root.addEventListener('keydown', () => { pointer = false; });

    rail.forEach((button, index) => button.addEventListener('click', () => goTo(index)));
    back.addEventListener('click', () => goTo(current - 1));
    next.addEventListener('click', () => goTo(current + 1));

    root.querySelector('.iconnect-hws__restart').addEventListener('click', () => {
        names.forEach((name) => delete answers[name]);
        root.querySelectorAll('.iconnect-hws__step input').forEach((input) => { input.checked = false; });
        refreshTiles();
        refreshSentence();
        refreshCount();
        refreshResults();
        goTo(0);
        root.querySelector('.iconnect-hws__panel').scrollIntoView({ behavior: motion ? 'smooth' : 'auto', block: 'start' });
    });

    refreshTiles();
});
